import { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useStructuredData } from '../hooks/useStructuredData';
import { useAuthContext } from '../contexts/AuthContext';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqSection {
  title: string;
  items: FaqItem[];
}

const FAQ_SECTIONS: FaqSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        q: 'What is Takedown League?',
        a: 'Takedown League is a free fantasy sports platform built specifically for NCAA wrestling. You draft real wrestlers to your team using a budget system, and your score is determined by the actual NCAA team points those wrestlers earn during the tournament. It works like traditional fantasy sports — everyone picks their own roster, and real tournament results decide the winner. There are no entry fees, no gambling, and no monetary stakes.',
      },
      {
        q: 'Is Takedown League free to use?',
        a: 'Yes, completely free. There are no entry fees, subscription charges, or paid advantages of any kind. Takedown League has no gambling element — you compete purely for bragging rights. The platform is supported by advertising, which keeps it free for everyone.',
      },
      {
        q: 'How do I sign up?',
        a: 'Create an account using your email address or sign in with Google. Email sign-ups require a confirmation code sent to your inbox. Google sign-ins are verified automatically. Account creation takes less than two minutes, and you can start joining leagues right away.',
      },
      {
        q: 'Do I need to download an app?',
        a: 'No download is required. Takedown League is a mobile-friendly web app that works on any device with a modern browser — iPhone, Android, tablet, or desktop. Simply visit takedownleague.com and sign in.',
      },
      {
        q: 'Do I need to know a lot about wrestling to play?',
        a: "Not at all. Takedown League is beginner-friendly. The drafting interface shows each wrestler's seed, school, and cost, giving you everything you need to make informed picks. If you can follow a tournament bracket, you can compete. Many players learn more about wrestling by following their drafted wrestlers through the tournament.",
      },
    ],
  },
  {
    title: 'Leagues & Commissioners',
    items: [
      {
        q: 'What is a league?',
        a: "A league is a private group of people competing against each other around a specific NCAA wrestling tournament. Each member drafts their own roster of wrestlers, and everyone's scores are tracked together on a shared leaderboard. Leagues can be any size — from a few friends to a large group from a wrestling team or fan community.",
      },
      {
        q: 'How do I create a league?',
        a: 'Sign into your account, go to the Leagues section, and click "Create League." Choose a name for your league, select the NCAA tournament it will follow, and set a budget for each team. After creating the league, you will receive an invite link and a join code to share with anyone you want to invite. There is no limit on the number of players.',
      },
      {
        q: 'How do I join a league?',
        a: "If someone shares an invite link with you, simply click the link and you will be added to the league. If you don't have an account yet, you will be prompted to create one first. You can also enter a join code manually from the Leagues page. The invite process is designed to be as simple as sharing a URL.",
      },
      {
        q: 'Can I be in multiple leagues at the same time?',
        a: 'Yes. You can join as many leagues as you want, across different tournaments or even multiple leagues for the same tournament. You can also have more than one team entry in the same league if the commissioner permits it.',
      },
      {
        q: 'What is a commissioner?',
        a: 'The commissioner is the person who created the league. They can set and adjust the team budget, configure the roster lock time, manage member approvals, and send private messages to league members. Think of the commissioner as the league manager.',
      },
      {
        q: 'Can someone join my league without an invite?',
        a: 'No. Your league is private and can only be joined using your specific invite link or join code. You control who has access to those credentials.',
      },
    ],
  },
  {
    title: 'Drafting Your Roster',
    items: [
      {
        q: 'How does drafting work?',
        a: 'Drafting in Takedown League is not turn-based like some fantasy sports platforms. Each player independently selects wrestlers for their own roster at any time before the roster lock deadline. You build your team at your own pace within your budget. Once the lock time passes, all rosters become visible to everyone in the league.',
      },
      {
        q: "How is a wrestler's cost determined?",
        a: "A wrestler's cost is based on their tournament seed and matches the team points they would earn by placing. A #1 seed costs $16, a #2 seed costs $12, a #3 seed costs $10, a #4 seed costs $9, a #5 seed costs $7, a #6 seed costs $6, a #7 seed costs $4, and an #8 seed costs $3. Wrestlers seeded higher than 8 (unseeded) cost $0. This alignment means drafting a top seed is a bigger investment but also a safer bet for points.",
      },
      {
        q: 'What is the recommended budget?',
        a: 'The default budget is $64, which is the exact cost to draft one wrestler from each seed position 1 through 8. The commissioner can adjust this. A higher budget gives teams more flexibility to stack top seeds; a tighter budget forces harder trade-offs.',
      },
      {
        q: 'How many wrestlers can I draft?',
        a: 'You may draft up to 10 wrestlers — one per weight class. There are 10 weight classes in NCAA Division I wrestling (125, 133, 141, 149, 157, 165, 174, 184, 197, and 285 lbs). You are not required to fill every weight class, and you can allocate your entire budget to fewer wrestlers if that fits your strategy.',
      },
      {
        q: 'Can I draft multiple wrestlers in the same weight class?',
        a: 'No. You may only select one wrestler per weight class. This is by design — it spreads competition across all weight classes and prevents any team from stacking a single class.',
      },
      {
        q: 'Can I change my roster after submitting it?',
        a: "Yes, you can update your roster at any time before the roster lock deadline. After the lock, rosters are final. You won't be able to make changes once the tournament begins, so plan accordingly.",
      },
      {
        q: 'Can I see other teams before the roster lock?',
        a: "No. All rosters are hidden from other league members until the lock time passes. This prevents anyone from copying another player's strategy before locking in their own picks.",
      },
    ],
  },
  {
    title: 'Scoring System',
    items: [
      {
        q: 'How is my score calculated?',
        a: 'Your score equals the total NCAA team points earned by all wrestlers on your roster during the tournament. Team points include three components: placement points (awarded at the end of the tournament based on final finish), advancement points (earned each time a wrestler wins a match), and bonus points (earned for dominant victories like pins, technical falls, and major decisions).',
      },
      {
        q: 'What are the placement points for each finishing position?',
        a: 'NCAA placement points are: 1st place = 16 pts, 2nd place = 12 pts, 3rd place = 10 pts, 4th place = 9 pts, 5th place = 7 pts, 6th place = 6 pts, 7th place = 4 pts, 8th place = 3 pts. Wrestlers who do not place in the top 8 do not earn placement points.',
      },
      {
        q: 'What are advancement points?',
        a: 'Advancement points are earned each time a wrestler wins a match and advances to the next round. They accumulate across every round a wrestler competes and wins. A wrestler who makes a deep run through both the championship and consolation brackets can earn substantial advancement points even without placing in the top 8. If a wrestler receives a bye and wins their next match, the bye counts as an advancement (1 point in the championship bracket, 0.5 in consolation). A bye without a subsequent win earns no points.',
      },
      {
        q: 'What are bonus points?',
        a: 'Bonus points are awarded for winning a match by a dominant margin. A pin (fall) earns the highest bonus, followed by a technical fall (winning by 15+ points), and then a major decision (winning by 8–14 points). A regular decision earns no bonus points. Wrestlers who win matches emphatically are worth more to your roster.',
      },
      {
        q: 'Are team penalty deductions applied to my score?',
        a: "No. NCAA team penalty deductions (such as unsportsmanlike conduct deductions) affect a school's overall team score but are not subtracted from individual wrestlers' fantasy points in Takedown League.",
      },
      {
        q: 'When are scores updated?',
        a: 'Scores are updated as tournament results come in throughout the event. You can check your league standings at any point during the tournament to see your current rank and how your wrestlers are performing.',
      },
    ],
  },
  {
    title: 'Tournaments',
    items: [
      {
        q: 'Which tournaments does Takedown League support?',
        a: 'Takedown League supports NCAA Division I wrestling tournaments, including Regional (national qualifier) tournaments and the NCAA National Championship. Regional tournaments typically take place in March, followed by the National Championship later in the month. We add each tournament to the platform as soon as the official field and seeds are announced.',
      },
      {
        q: 'When can I create a league for a tournament?',
        a: 'You can create a league as soon as seeds are announced for a tournament. Leagues cannot be created before the field is set because wrestler costs are based on their official seeds. Watch for seed announcements from the NCAA — usually one to two weeks before each tournament.',
      },
      {
        q: 'Where can I watch the matches?',
        a: 'Tournament pages on Takedown League include links to FloWrestling, the primary broadcast partner for NCAA wrestling, and WrestleStat for detailed match statistics and results. Live coverage of most rounds is available through FloWrestling.',
      },
      {
        q: 'What weight classes are included?',
        a: 'NCAA Division I wrestling includes 10 weight classes: 125, 133, 141, 149, 157, 165, 174, 184, 197, and 285 pounds. All 10 are included in Takedown League, and your roster can have one wrestler from each.',
      },
    ],
  },
  {
    title: 'Strategy Tips',
    items: [
      {
        q: 'Should I always draft the top seeds?',
        a: 'Not necessarily. Top seeds are the most expensive picks and, while they usually perform well, they offer less upside than a mid-seed who outperforms expectations. Drafting all top seeds is a low-risk, moderate-reward strategy. Many experienced players find success mixing a few high-confidence top seeds with several mid-seeds who have strong upset potential.',
      },
      {
        q: 'What makes a strong roster?',
        a: "Strong rosters usually balance reliability with value. That means taking a few top seeds in weight classes where you're confident in the outcome, and using remaining budget on mid-seeds who could earn disproportionate points through an upset run. Wrestlers in competitive weight classes where the top seed is beatable can offer excellent value. Pay attention to wrestlers who have beaten higher seeds earlier in the season.",
      },
      {
        q: 'What happens if a wrestler I drafted gets injured or scratches?',
        a: 'If a wrestler withdraws before competing, they score zero points. Roster locks prevent changes once the tournament begins, so there is no way to replace a scratched wrestler. This is an unavoidable risk of fantasy sports. Some players hedge against this by drafting mid-seeds with solid injury records rather than betting heavily on a single expensive top seed.',
      },
    ],
  },
];

export default function FaqPage() {
  const { isAuthenticated } = useAuthContext();
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  useDocumentMeta(
    'FAQ',
    'Frequently asked questions about Takedown League: how to play fantasy wrestling, drafting NCAA wrestlers, scoring, leagues, and tournament strategy.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_SECTIONS.flatMap((section) =>
      section.items.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: a,
        },
      }))
    ),
  });

  function toggle(key: string) {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  const midpoint = Math.ceil(FAQ_SECTIONS.length / 2);

  return (
    <PublicLayout>
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-purple-100">
            Everything you need to know about playing Takedown League.
          </p>
        </div>
      </div>

      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto space-y-10">
          {FAQ_SECTIONS.slice(0, midpoint).map((section) => (
            <FaqSection
              key={section.title}
              section={section}
              openItems={openItems}
              toggle={toggle}
            />
          ))}

          <div className="my-8">
            <AdBanner slot={AD_SLOTS.faq} format="horizontal" />
          </div>

          {FAQ_SECTIONS.slice(midpoint).map((section) => (
            <FaqSection
              key={section.title}
              section={section}
              openItems={openItems}
              toggle={toggle}
            />
          ))}
        </div>
      </section>

      {/* Still have questions */}
      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Still have questions?</h2>
          <p className="text-gray-600 mb-6">
            Check our Help Center or reach out and we will get back to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://takedownleague.tawk.help/category/getting-started"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Visit Help Center
            </a>
            <Link
              to="/contact"
              className="inline-block bg-white border border-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {isAuthenticated ? (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Ready to compete?</h2>
              <p className="text-purple-100 mb-6">
                Head back to your leagues to manage your rosters.
              </p>
              <Link
                to="/leagues"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Back to My Leagues
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Ready to play?</h2>
              <p className="text-purple-100 mb-6">
                Create your free account and start drafting your wrestling roster today.
              </p>
              <Link
                to="/login?tab=register"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Create Your Free Account
              </Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}

function FaqSection({
  section,
  openItems,
  toggle,
}: {
  section: FaqSection;
  openItems: Set<string>;
  toggle: (key: string) => void;
}) {
  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        {section.title}
      </h2>
      <dl className="space-y-3">
        {section.items.map(({ q, a }) => {
          const key = `${section.title}:${q}`;
          const open = openItems.has(key);
          return (
            <div key={q} className="border border-gray-200 rounded-lg overflow-hidden">
              <dt>
                <button
                  onClick={() => toggle(key)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
                  aria-expanded={open}
                >
                  <span className="font-medium text-gray-900 pr-4">{q}</span>
                  <span className="flex-shrink-0 text-gray-400 text-lg leading-none">
                    {open ? '−' : '+'}
                  </span>
                </button>
              </dt>
              {open && (
                <dd className="px-5 py-4 bg-gray-50 border-t border-gray-200">
                  <p className="text-gray-700 leading-relaxed">{a}</p>
                </dd>
              )}
            </div>
          );
        })}
      </dl>
    </div>
  );
}
